import _ from 'lodash';
import React, { RefObject, useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { Alert, Button, Col, Grid, Row } from 'react-bootstrap';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { Car } from '@upstart/harmony/lib/legos/carTypes';
import {
  FinanceResponse,
  FinanceResponseData,
} from '@upstart/harmony/lib/legos/financingTypes';
import { UPSTART_MIN_FINANCE_AMOUNT } from '@upstart/harmony/lib/legos/constants';
import { fmtMoney } from '@upstart/harmony/lib/formatter';
import {
  isEmbeddedInClosingWorkflow,
  isEmbeddedInCommandCenter,
} from 'util/commandCenter';
import { getCarInfo, sendData } from 'util/amplitude';
import { amplitudeEvents } from 'util/amplitudeEvents';
import LoadingOverlay from '../../../components/LoadingOverlay';
import CDKSaveDealChallengeModal from './CDKSaveDealChallengeModal';
import SchemaBasedRICForm from './SchemaBasedRicForm';
import { Feature, useGrowthBook } from '../../../hooks/GrowthBook';

const IS_EMBEDDED_IN_CC = isEmbeddedInCommandCenter();

const SAVE_BUTTON_TEXT = IS_EMBEDDED_IN_CC
  ? 'Save and start eContracting'
  : 'Save and Start eContracting';

const CANCEL_BUTTON_TEXT = IS_EMBEDDED_IN_CC
  ? 'Cancel and revert changes'
  : 'Cancel and Revert Changes';

export function RICForm(props: {
  response: FinanceResponse;
  fetchResponseWithUpdatedData: (data: Partial<FinanceResponseData>) => void;
  onCancel: () => void;
  loading: boolean;
  fetchingError: string | null;
  isDMSFlowEnabled: boolean;
  deal?: Deal;
  onPullFromDMS: () => void;
  callReviewFinancing: () => Promise<void>;
  showCDKSaveDealNotice: boolean;
  state?: string | null;
  car?: Car;
  formRef?: RefObject<HTMLFormElement>;
}) {
  const {
    response,
    fetchResponseWithUpdatedData,
    isDMSFlowEnabled,
    deal,
    onPullFromDMS,
    callReviewFinancing,
    showCDKSaveDealNotice,
    fetchingError,
    state,
    car,
    formRef,
  } = props;
  const gb = useGrowthBook();

  // CDK Modal Challenge to pull from CDK
  const [showSaveDealInCDKChallenge, setShowSaveDealInCDKChallenge] = useState<
    boolean
  >(false);

  const {
    hasDuplicateTradeIn,
    isInvalid,
    isFinanceAmountOverLimit,
    isFinanceAmountUnderLimit,
    isBackendOverLimit,
    isLTVBelowMinimumForGap,
    isGapPremiumOverLimit,
    maxGapPremium,
    minFinanceAmountLimit,
  } = response.validations ?? {};
  const { currentRate } = response ?? {};
  const responseData = response.data ?? {};

  const hasGapRelatedError = !!(
    isLTVBelowMinimumForGap || isGapPremiumOverLimit
  );

  const fetchingErrorAlert = <Alert bsStyle="danger">{fetchingError}</Alert>;

  const customerID = `${_.get(deal, 'applicant.firstName', '')} ${_.get(
    deal,
    'applicant.lastName',
    '',
  )}`;

  useEffect(() => {
    sendData(amplitudeEvents.DESKING_EDIT_RIC_START, getCarInfo(car));
  }, []);

  const formatUpdatedDate = () => {
    const lastUpdated = _.get(deal, 'updatedAt', '');
    if (lastUpdated) {
      return moment(lastUpdated).format('YYYY/MM/DD h:mm A');
    }
    return '-';
  };
  const formattedUpdatedDate = formatUpdatedDate();

  const getDMSHeader = () => (
    <Row>
      <Col sm={12}>
        <div className="dms-form-header">
          <div className="dms-info">
            <span className="dms-info-desc">Deal ID:</span>
            <span className="dms-info-value">
              {_.get(deal, 'financeMeta.dmsDealInfo.id', '')}
            </span>
          </div>
          <div className="dms-info">
            <span className="dms-info-desc">Customer ID:</span>
            <span className="dms-info-value" data-dd-privacy="mask">
              {customerID}
            </span>
          </div>
          <div className="dms-info">
            <span className="dms-info-desc">Last Updated:</span>
            <span className="dms-info-value">{formattedUpdatedDate}</span>
          </div>
          <Button
            type="button"
            className="btn btn-primary pull-from-dms-button"
            onClick={onPullFromDMS}
          >
            Pull from DMS
          </Button>
        </div>
      </Col>
    </Row>
  );

  const getForm = () => (
      <SchemaBasedRICForm
        responseData={responseData}
        fetchResponseWithUpdatedData={fetchResponseWithUpdatedData}
        state={state ?? undefined}
      />
    );

  const onFormSubmit = (event: any) => {
    event.preventDefault();
    // if dealer does not have dmsEnabled OR
    // Deal was updated in CDK continue with regular flow - submit
    // and the DMSType is CDK
    if (
      isDMSFlowEnabled &&
      showCDKSaveDealNotice &&
      !isEmbeddedInClosingWorkflow()
    ) {
      // prevent it from running the submit function on CarPencilEditor
      event.stopPropagation();
      // If DMS is enabled and the deal was not updated - challenge the user
      setShowSaveDealInCDKChallenge(true);
    } else {
      // Otherwise submit right away
      callReviewFinancing();
    }
  };

  return (
    <form
      onSubmit={onFormSubmit}
      data-dd-privacy="allow"
      ref={formRef}
      data-testid="ric-form"
    >
      <div>
        {!isEmbeddedInClosingWorkflow() && (
          <div className="flex">
            <h3 className="pr-15">Retail Installment Sale Contract</h3>
          </div>
        )}
        <LoadingOverlay
          size="small"
          className="bottom-loading-indicator"
          active={props.loading}
          inline={false}
        />
        <div
          className="upstart-offer-container"
          style={
            isEmbeddedInClosingWorkflow()
              ? { padding: 0, boxShadow: 'none' }
              : {}
          }
        >
          <Grid>
            {isDMSFlowEnabled &&
              !isEmbeddedInClosingWorkflow() &&
              getDMSHeader()}
            <Row>
              <Col sm={12}>
                {!_.isEmpty(fetchingError) && fetchingErrorAlert}
              </Col>
            </Row>
            <Row>
              <Col md={12} lg={10}>
                <div className="form-container">{getForm()}</div>
              </Col>
            </Row>
            {!gb.isFeatureEnabled(Feature.SHOW_RIC_VALIDATIONS_IN_CC) && (
              <>
                {!_.isEmpty(fetchingError) && fetchingErrorAlert}
                {isFinanceAmountOverLimit && (
                  <Alert bsStyle="danger">
                    Based on your recent change, the current loan amount exceeds
                    the maximum approved loan amount. Please make adjustments to
                    fall back into the approved loan amount.
                  </Alert>
                )}
                {isFinanceAmountUnderLimit && (
                  <Alert bsStyle="danger" data-testid="financeAmountUnderLimit">
                    Based on your recent change, the current loan amount does
                    not meet the minimum allowed loan amount (
                    {fmtMoney(
                      minFinanceAmountLimit ?? UPSTART_MIN_FINANCE_AMOUNT,
                      2,
                    )}
                    ). Please make adjustments to fall back into the approved
                    loan amount.
                  </Alert>
                )}
                {isBackendOverLimit &&
                  response.calculatedBackendAmount &&
                  currentRate?.maxBackendAmount && (
                    <Alert bsStyle="danger">
                      Based on your recent change, the current backend amount of{' '}
                      {fmtMoney(response.calculatedBackendAmount, 2)} exceeds
                      the maximum approved backend amount (
                      {fmtMoney(currentRate.maxBackendAmount)}). Please make
                      adjustments to fall back into the approved backend amount.
                    </Alert>
                  )}
                {hasDuplicateTradeIn && (
                  <Alert bsStyle="danger">
                    Multiple trade-ins with duplicate VINs are not allowed.
                  </Alert>
                )}
                {showSaveDealInCDKChallenge && (
                  <CDKSaveDealChallengeModal
                    setShowSaveDealInCDKChallenge={
                      setShowSaveDealInCDKChallenge
                    }
                    submitFn={callReviewFinancing}
                  />
                )}
                {hasGapRelatedError &&
                  ((isLTVBelowMinimumForGap && (
                    <Alert bsStyle="danger">
                      State law does not allow for a GAP waiver to be added to a
                      vehicle with the current vehicle’s LTV. Please remove the
                      GAP Waiver in order to proceed with this loan.
                    </Alert>
                  )) ||
                    (isGapPremiumOverLimit && (
                      <Alert bsStyle="danger">
                        GAP waiver exceeds state allowable limits of premium
                        amount to amount financed. Please remove the GAP Waiver
                        or reduce the premium amount to be less than{' '}
                        {fmtMoney(maxGapPremium ?? 0, 2)} in order to proceed
                        with this loan.
                      </Alert>
                    )))}
              </>
            )}
            {!isEmbeddedInClosingWorkflow() && (
              <Row>
                <Col md={6} xs={12} className="button-footer">
                  <Button className="cancel-button" onClick={props.onCancel}>
                    {CANCEL_BUTTON_TEXT}
                  </Button>
                  <Button
                    data-testid="ric-form-submit-button"
                    type="submit"
                    bsStyle="primary"
                    className="submit-button"
                    disabled={props.loading || isInvalid || hasDuplicateTradeIn}
                  >
                    {SAVE_BUTTON_TEXT}
                  </Button>
                </Col>
              </Row>
            )}
          </Grid>
        </div>
      </div>
    </form>
  );
}

export default RICForm;
